import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-lg border px-2.5 py-0.5 text-xs font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow-sm [a&]:hover:bg-primary/80",
        destructive:
          "border-transparent bg-red-100 text-red-900 [a&]:hover:bg-red-100/80 focus-visible:ring-destructive/20",
        // Soft amber nudge, e.g. "Recommended". Same palette as `warning`, kept apart so a nudge is not a status.
        highlight: "border-transparent bg-amber-100 text-amber-900 [a&]:hover:bg-amber-100/80",
        // In progress, nothing wrong and nothing to do yet.
        info: "border-transparent bg-blue-100 text-blue-900 [a&]:hover:bg-blue-100/80",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/80",
        step: "border-transparent bg-stone-200 text-neutral-950 [a&]:hover:bg-stone-200/80",
        success: "border-transparent bg-green-100 text-green-900 [a&]:hover:bg-green-100/80",
        warning: "border-transparent bg-amber-100 text-amber-900 [a&]:hover:bg-amber-100/80",
      },
    },
  },
);

interface BadgeProps extends ComponentProps<"span">, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({ asChild = false, className, variant, ...props }: BadgeProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "span";

  return <Comp className={cn(badgeVariants({ variant }), className)} data-slot="badge" {...props} />;
}

export { Badge, type BadgeProps, badgeVariants };
